import React, { useState } from 'react';
import {
  MusicalNoteIcon, TrashIcon, PencilIcon, ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { IMusicGeneration, TaskStatus } from '@packages/global';
import MusicTitleEditor from './MusicTitleEditor';
import { musicApi } from '../../services/user/music.api';
import AudioPlayer from '../common/AudioPlayer';

interface Props {
  item: IMusicGeneration;
  modelName: string;
  onReuse: (item: IMusicGeneration) => void;
  onDelete: (id: string) => void;
  onDownload: (item: IMusicGeneration) => void;
  onTitleChange?: (id: string, title: string) => void;
}

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
}

const STATUS_STYLES: Record<string, string> = {
  [TaskStatus.PENDING]: 'bg-bg-elevated text-gray-300 border border-border-subtle',
  [TaskStatus.SUBMITTED]: 'bg-warning/20 text-warning border border-warning/30',
  [TaskStatus.PROCESSING]: 'bg-info/20 text-info border border-info/30',
  [TaskStatus.COMPLETED]: 'bg-success/20 text-success border border-success/30',
  [TaskStatus.FAILED]: 'bg-error/20 text-error border border-error/30',
};

export default function MusicCard({ item, modelName, onReuse, onDelete, onDownload, onTitleChange }: Props) {
  const { t, i18n } = useTranslation(['generation', 'common']);
  const isProcessing = item.status === TaskStatus.PENDING || item.status === TaskStatus.PROCESSING || item.status === TaskStatus.SUBMITTED;
  const [localTitle, setLocalTitle] = useState<string | undefined>(item.title);

  function formatDate(date: string | Date) {
    return new Date(date).toLocaleString(i18n.language, { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  const handleSaveTitle = async (id: string, title: string) => {
    await musicApi.updateMusicGenerationTitle(id, title);
    setLocalTitle(title || undefined);
    onTitleChange?.(id, title);
  };

  return (
    <div className="bg-bg-surface rounded-xl border border-border-subtle hover:border-primary/50 hover:shadow-[0_0_16px_rgba(255,92,0,0.12)] transition-all duration-300 overflow-hidden flex flex-col">
      {/* 头部 */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center flex-shrink-0">
            <MusicalNoteIcon className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <MusicTitleEditor
              id={item.id}
              title={localTitle}
              fallback={item.id.slice(0, 8)}
              onSave={handleSaveTitle}
            />
            <p className="text-xs text-gray-500 truncate">{modelName}</p>
          </div>
        </div>
        <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full flex-shrink-0 backdrop-blur-sm ${STATUS_STYLES[item.status] ?? ''}`}>
          {t(`generation:status.${item.status}`)}
        </span>
      </div>

      {/* 描述 */}
      <div className="px-4 pb-2 flex-1">
        <p className="text-sm text-gray-300 line-clamp-2">{item.prompt}</p>
        {/* 参数标签 */}
        <div className="flex flex-wrap gap-1 mt-2">
          {item.bpm && (
            <span className="px-1.5 py-0.5 text-xs bg-bg-elevated text-gray-400 rounded">{item.bpm} BPM</span>
          )}
          {item.keyscale && (
            <span className="px-1.5 py-0.5 text-xs bg-bg-elevated text-gray-400 rounded">{item.keyscale}</span>
          )}
          {item.language && (
            <span className="px-1.5 py-0.5 text-xs bg-bg-elevated text-gray-400 rounded">{item.language.toUpperCase()}</span>
          )}
          {item.generationMode === 'professional' && (
            <span className="px-1.5 py-0.5 text-xs bg-blue-600/20 text-blue-400 rounded">{t('generation:music.card.professional')}</span>
          )}
        </div>
      </div>

      {/* 音频播放器 */}
      {item.status === TaskStatus.COMPLETED && item.resultAudioUrl && (
        <div className="px-4 pb-2">
          <AudioPlayer src={item.resultAudioUrl} />
        </div>
      )}

      {/* 处理中动画 */}
      {isProcessing && (
        <div className="px-4 pb-2 flex items-center gap-2">
          <div className="flex gap-0.5">
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                className="w-1 bg-primary rounded-full animate-pulse"
                style={{ height: `${8 + i * 4}px`, animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
          <span className="text-xs text-gray-400">
            {item.status === TaskStatus.PENDING ? t('generation:music.card.waiting') : t('generation:music.card.generating')}
          </span>
        </div>
      )}

      {/* 失败信息 */}
      {item.status === TaskStatus.FAILED && item.resultMessage && (
        <div className="px-4 pb-2">
          <p className="text-xs text-red-400 line-clamp-2" title={item.resultMessage}>{item.resultMessage}</p>
        </div>
      )}

      {/* 底部 */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border-subtle">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{formatDuration(item.duration)}</span>
          <span>·</span>
          <span>{formatDate(item.createdAt)}</span>
        </div>
        <div className="flex gap-1">
          {item.status === TaskStatus.COMPLETED && item.resultAudioUrl && (
            <button
              onClick={() => onDownload(item)}
              className="p-1.5 rounded-lg bg-bg-elevated hover:bg-border-default text-gray-300 hover:text-white transition-all cursor-pointer"
              title={t('generation:music.card.downloadAudio')}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => onReuse(item)}
            className="p-1.5 rounded-lg bg-bg-elevated hover:bg-border-default text-gray-300 hover:text-white transition-all cursor-pointer"
            title={t('generation:music.card.reuseParams')}
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="p-1.5 rounded-lg bg-bg-elevated hover:bg-red-500/80 text-gray-300 hover:text-white transition-all cursor-pointer"
            title={t('common:delete')}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
